import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import Container from 'components/UI/Container';
import Title from 'components/UI/Title';
import Subtitle from 'components/UI/Subtitle';
import Tag from 'components/UI/Tag';
import Button from 'components/UI/Button';
import StepsModal from './StepsModal';
import { useTrans } from 'utils/localisation';
import { useStateModal, useWindowResize } from 'utils/hooks';
import { pushCustomEvent } from 'utils/analytics';
import { recaptchSrc } from 'define/scripts';
import { appendScriptSrc } from 'utils/scripts';
import { observer, pricingCalculator as store } from 'store';
import * as styles from './MobileCalculator.module.scss';

function MobileCalculator({ className, title, subtitle, sections }) {
  const { width } = useWindowResize();
  const t = useTrans(['pricingCalculator']);
  const { modalState, showModal, hideModal } = useStateModal();
  const [active, setActive] = useState(null);
  const [triggerValidation, setTriggerValidation] = useState(false);

  const handleInputChange = id => () => {
    setActive(id);
  };

  const openModal = () => {
    if (!active) {
      setTriggerValidation(true);
      return;
    }

    const scriptLoaded = document.getElementById('recaptcha');

    if (!scriptLoaded) {
      appendScriptSrc(recaptchSrc, 'body', 'recaptcha');
    }

    if (active !== store.previouslyOpened) {
      store.setPayload({});
    }
    store.setPreviouslyOpened(active);
    store.setStep(1);

    showModal();
    pushCustomEvent('pricingMobile', { action: 'modelOpen', label: active });
  };

  const closeModal = () => {
    hideModal();
    pushCustomEvent('pricingMobile', { action: 'modelClosed', label: active });
  };

  const resetFirstStep = () => setActive(null);

  const onAnimationEnd = e => {
    const lastChildIndex = e.target.parentElement.children.length - 1;

    if (e.target === e.target.parentElement.children[lastChildIndex]) {
      setTriggerValidation(false);
    }
  };

  useEffect(() => {
    if (width > 545) {
      hideModal();
    }
  }, [width]);

  return (
    <>
      <Container className={className}>
        <div
          className={styles.wrapper}
          onClick={() => pushCustomEvent('pricingMobile', { action: 'startEngage' })}
        >
          <Title>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>
          <div className={styles.inputsWrapper} onAnimationEnd={onAnimationEnd}>
            {sections.map(({ id, button }, i) => (
              <Tag
                key={id}
                className={cn(styles.tag, triggerValidation && styles.validate)}
                type="button"
                active={active === id}
                theme="calculatorTag"
                mods="withoutMT"
                onClick={handleInputChange(id)}
                style={{ '--animationDelay': `${i * 500}ms` }}
              >
                {button}
              </Tag>
            ))}
          </div>
          <Button className={styles.button} fullWidthMobile onClick={openModal}>
            {t('next')}
          </Button>
        </div>
      </Container>
      <StepsModal
        isOpen={modalState.isOpen}
        closeModalCallback={closeModal}
        resetFirstStep={resetFirstStep}
        {...sections.find(({ id }) => id === active)}
      />
    </>
  );
}

export default observer(MobileCalculator);
